import { useEffect, useRef, type RefObject } from 'react';
import { labels } from './labels';

/**
 * QA-99: **Newer** and **Older**, each shown only when it has somewhere to go, and nothing when
 * neither has. `Pager` (the cursor-stack lists) and the **Extraction rules** version list share it.
 *
 * Focus: a click records which button the operator used (not `document.activeElement`, which
 * Safari does not move on a mouse click). On the first render after that click that is not `busy`,
 * focus goes to the same button if it shows, else the other one, else `focusTarget`. A render with
 * no click on record (a poll, a reload, a new `version`) never moves focus. A list that loads a page
 * passes `busy` while it loads, so this component stays mounted and keeps the record.
 */
export function PagerButtons({
  canNewer,
  canOlder,
  onNewer,
  onOlder,
  busy = false,
  focusTarget,
}: {
  canNewer: boolean;
  canOlder: boolean;
  onNewer: () => void;
  onOlder: () => void;
  busy?: boolean;
  focusTarget?: RefObject<HTMLElement | null>;
}) {
  const newerRef = useRef<HTMLButtonElement>(null);
  const olderRef = useRef<HTMLButtonElement>(null);
  const clicked = useRef<'newer' | 'older' | null>(null);

  useEffect(() => {
    if (busy || !clicked.current) return;
    const [same, other] = clicked.current === 'newer' ? [newerRef, olderRef] : [olderRef, newerRef];
    (same.current ?? other.current ?? focusTarget?.current)?.focus();
    clicked.current = null;
  });

  if (busy || (!canNewer && !canOlder)) return null;
  return (
    <p className="action-row flow-md">
      {canNewer && (
        <button
          ref={newerRef}
          type="button"
          className="btn"
          onClick={() => {
            clicked.current = 'newer';
            onNewer();
          }}
        >
          {labels.paging.newer}
        </button>
      )}
      {canOlder && (
        <button
          ref={olderRef}
          type="button"
          className="btn"
          onClick={() => {
            clicked.current = 'older';
            onOlder();
          }}
        >
          {labels.paging.older}
        </button>
      )}
    </p>
  );
}
